import React, { ReactNode } from 'react';
import { Flex, Box, Stack, Text } from '@atlaskit/primitives';
import { B400 } from '@atlaskit/theme/colors';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { isActiveEntitlement, validateFreeSubscriptions } from '@css-xp/entitlements-v2';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { convertBillingFrequencyToAdverb, isMultiInstanceOffering } from '@css-xp/ppc-utils';
import { cardStyles, HeadingText } from './entitlement-cards.styles';

export type PlanProps = {
  entitlement: EntitlementDetails;
  isMultiInstanceAppsEnabled?: boolean;
};

export const Plan = ({ entitlement, isMultiInstanceAppsEnabled }: PlanProps) => {
  const { offering, billingFrequency } = entitlement;

  const isActive = isActiveEntitlement(entitlement);
  const isUpgradeTrial = !!entitlement.upgradeTrial;
  const isFreePlan = validateFreeSubscriptions(entitlement);
  const isMultiInstanceAppInstanceEntitlement =
    isMultiInstanceAppsEnabled &&
    entitlement?.productId &&
    isMultiInstanceOffering(entitlement?.offering, entitlement.productId);

  let headingText = offering?.name ?? 'N/A';
  let chargedText: string | ReactNode | null = null;

  if (isMultiInstanceAppInstanceEntitlement) {
    headingText = `${headingText} (Included with Multi-instance)`;
  }

  if (isFreePlan) {
    chargedText = <Text color="color.text.subtle">No charge</Text>;
  }

  if (isActive && !isFreePlan) {
    if (isUpgradeTrial) {
      const upgradeTrial = entitlement?.upgradeTrial;

      chargedText = (
        <Flex alignItems="center" gap="space.050">
          <InfoIcon size="small" label="upgrade-trial-info" primaryColor={B400} />
          <Flex direction="column">
            {billingFrequency && (
              <Text color="color.text.subtlest">
                Billed {convertBillingFrequencyToAdverb(billingFrequency).toLowerCase()}
              </Text>
            )}
            <Text color="color.text.subtlest">{upgradeTrial?.offeringName || 'Premium'} trial</Text>
          </Flex>
        </Flex>
      );
    } else if (billingFrequency) {
      chargedText = `Billed ${convertBillingFrequencyToAdverb(billingFrequency).toLowerCase()}`;
    }
  }

  return (
    <Box padding="space.200" xcss={cardStyles}>
      <Stack space="space.100" alignBlock="center">
        <Text size="small" color="color.text.subtlest">
          Plan
        </Text>
        <HeadingText>{headingText}</HeadingText>
        <Text color="color.text.subtle">{chargedText}</Text>
      </Stack>
    </Box>
  );
};
